import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, FileText, FileSpreadsheet, Image as ImageIcon, Pencil, ShieldCheck, Eye, EyeOff, Monitor, FileWarning } from "lucide-react";
import { api, coachHeaders } from "../lib/api";
import { PlayerFormDialog } from "./PlayerFormDialog";
import { EncadrantDialog } from "./EncadrantDialog";
import { ExportColumnsPicker } from "./ExportColumnsPicker";
import { PlayerSheet } from "./PlayerSheet";
import { CatBadge } from "./CatBadge";
import { categorieOf, joueurKey } from "../lib/categorie";
import { AddPersonDialog } from "./AddPersonDialog";
import { envoyerFicheParent, envoyerFicheEncadrant } from "./FichesRecues";
import { santeLignes, santeVal, adresseComplete } from "../lib/sante";
import { exportTableauA4, exportFichesA4, decouper, TITRE, CONFIDENTIEL } from "../lib/pdfA4";

const errMsg = (e) => e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Action impossible";
const frDate = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(iso ?? "") ? iso.split("-").reverse().join("/") : "");
export const tailleTxt = (j) => [j.taille_maillot, frDate(j.taille_date)].filter(Boolean).join(" - ");
const age = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso ?? "")) return null;
  const b = new Date(iso), n = new Date();
  return n.getFullYear() - b.getFullYear() - (n < new Date(n.getFullYear(), b.getMonth(), b.getDate()) ? 1 : 0);
};
export const urgenceOf = (j) => {
  if (j.urgence_choix === "autre") return { lien: j.urgence_lien || "Autre", nom: j.urgence_nom, tel: j.urgence_tel, tel2: j.urgence_tel2, adresse: j.urgence_adresse };
  const r1 = { lien: j.resp1_lien || "Responsable 1", nom: j.responsable, tel: j.telephone, tel2: j.urgence_tel2, adresse: j.resp1_adresse };
  const r2 = { lien: j.resp2_lien || "Responsable 2", nom: j.resp2_nom, tel: j.resp2_telephone, tel2: j.urgence_tel2, adresse: j.resp2_adresse };
  if (j.urgence_choix === "resp1") return r1;
  if (j.urgence_choix === "resp2") return r2;
  const lien = { papa: "Papa", maman: "Maman" }[j.urgence_choix];
  if (!lien) return null;
  if (j.resp1_lien === lien) return r1;
  if (j.resp2_lien === lien) return r2;
  return { lien, nom: "responsable non renseigné" };
};
const medecinTxt = (j) => [j.medecin_nom, j.medecin_tel, j.medecin_adresse].filter(Boolean).join(" – ");

const urgenceCol = (k) => (j) => urgenceOf(j)?.[k] ?? "";

// Colonnes dans l'ordre de la fiche de renseignements : enfant, responsables, santé, médecin, urgence, divers.
const EXPORT_COLS = [
  ["Fonction", (j) => j.fonction_staff ?? "Joueur", 12],
  ["Nom", (j) => j.nom.toUpperCase(), 18], ["Prénom", (j) => j.prenom, 14], ["Naissance", (j) => frDate(j.date_naissance), 11], ["Catégorie", categorieOf, 9],
  ["Adresse", (j) => j.adresse, 28], ["CP / Ville", (j) => j.cp_ville, 18],
  ["Resp. 1 lien", (j) => j.resp1_lien, 10], ["Resp. 1", (j) => j.responsable, 20], ["Tél. 1", (j) => j.telephone, 14], ["E-mail 1", (j) => j.email, 26], ["Adresse resp. 1", (j) => j.resp1_adresse, 28],
  ["Resp. 2 lien", (j) => j.resp2_lien, 10], ["Resp. 2", (j) => j.resp2_nom, 20], ["Tél. 2", (j) => j.resp2_telephone, 14], ["E-mail 2", (j) => j.resp2_email, 26], ["Adresse resp. 2", (j) => j.resp2_adresse, 28],
  ["Problème de santé", (j) => santeVal(j, "sante_probleme", "sante_details"), 28], ["Traitement", (j) => santeVal(j, "traitement", "traitement_details"), 28], ["Allergies", (j) => santeVal(j, "allergies", "allergies_details"), 28],
  ["Autres infos médicales", (j) => j.medical, 32], ["Médecin", medecinTxt, 32],
  ["Urgence lien", urgenceCol("lien"), 12], ["Urgence nom", urgenceCol("nom"), 20], ["Urgence tél.", urgenceCol("tel"), 14], ["Urgence tél. 2", urgenceCol("tel2"), 14], ["Urgence adresse", urgenceCol("adresse"), 28],
  ["Taille maillot", tailleTxt, 16], ["Remarques", (j) => j.remarques, 30], ["Fiche reçue", (j) => frDate(j.fiche_date), 11],
];
const EXPORT_LABELS = EXPORT_COLS.map(([l]) => l);
const ENFANT = ["Nom", "Prénom", "Naissance", "Catégorie"];
const URGENCE_COLS = ["Urgence lien", "Urgence nom", "Urgence tél.", "Urgence tél. 2"];
const PRESETS = [
  { name: "Complet", cols: EXPORT_LABELS },
  { name: "Liste simple", cols: ["Fonction", ...ENFANT] },
  { name: "Contacts parents", cols: ["Fonction", "Nom", "Prénom", "Resp. 1 lien", "Resp. 1", "Tél. 1", "E-mail 1", "Resp. 2 lien", "Resp. 2", "Tél. 2", "E-mail 2"] },
  { name: "Fiche médicale", cols: [...ENFANT, "Problème de santé", "Traitement", "Allergies", "Autres infos médicales", "Médecin", ...URGENCE_COLS] },
  { name: "Urgences", cols: [...ENFANT, ...URGENCE_COLS] },
  { name: "Fiches manquantes", cols: ["Nom", "Prénom", "Catégorie", "Resp. 1 lien", "Resp. 1", "Tél. 1", "Fiche reçue"], sansFiche: true },
];
const COLS_KEY = "effectif-export-colonnes-v2";
const loadExportCols = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(COLS_KEY));
    return Array.isArray(saved) ? EXPORT_LABELS.filter((l) => saved.includes(l)) : EXPORT_LABELS;
  } catch { return EXPORT_LABELS; }
};
const staffAsRow = (s) => ({ ...s, prenom: "", fonction_staff: s.fonction || "Encadrement", staff: true });
const rowsFor = (list, cols) => list.map((j) => cols.map(([, f]) => f(j) || ""));
const fileName = (ext) => `effectif-usc-jemappes-u12c.${ext}`;
const slugNom = (j) => `${j.nom}-${j.prenom}`.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Rubriques de la fiche individuelle A4 (ordre de la fiche de renseignements).
const SECTIONS = [
  ["Enfant", ["Fonction", "Nom", "Prénom", "Naissance", "Catégorie", "Adresse", "CP / Ville"]],
  ["Responsable 1", ["Resp. 1 lien", "Resp. 1", "Tél. 1", "E-mail 1", "Adresse resp. 1"]],
  ["Responsable 2", ["Resp. 2 lien", "Resp. 2", "Tél. 2", "E-mail 2", "Adresse resp. 2"]],
  ["Santé", ["Problème de santé", "Traitement", "Allergies", "Autres infos médicales", "Médecin"]],
  ["Contact d'urgence", ["Urgence lien", "Urgence nom", "Urgence tél.", "Urgence tél. 2", "Urgence adresse"]],
  ["Divers", ["Taille maillot", "Remarques", "Fiche reçue"]],
];

// Fiche A4 d'un encadrant : pas de responsables, ses propres coordonnées.
const colsDe = (labels) => EXPORT_COLS.filter(([l]) => labels.includes(l));
const STAFF_COLS = [
  ...colsDe(["Fonction", "Nom", "Naissance", "Adresse"]),
  ["Téléphone", (j) => j.telephone], ["E-mail", (j) => j.email],
  ...colsDe(["Problème de santé", "Traitement", "Allergies", "Autres infos médicales", "Médecin", ...URGENCE_COLS, "Urgence adresse", "Taille maillot", "Remarques", "Fiche reçue"]),
];
const STAFF_SECTIONS = [
  ["Encadrant", ["Fonction", "Nom", "Naissance", "Adresse", "Téléphone", "E-mail"]],
  ...SECTIONS.slice(3),
];

const A4_W = 1123, A4_H = 794, PX_PAR_CAR = 5.6;

const exportExcel = async (list, cols) => {
  const XLSX = await import("xlsx");
  const ws = XLSX.utils.aoa_to_sheet([cols.map(([l]) => l), ...rowsFor(list, cols), [], [CONFIDENTIEL]]);
  ws["!cols"] = cols.map(([, , wch]) => ({ wch }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Effectif");
  XLSX.writeFile(wb, fileName("xlsx"));
};

const LOGO = "/img/logo-usc.png";

const exportJpeg = async (node) => {
  const { toJpeg } = await import("html-to-image");
  const pages = [...node.querySelectorAll("[data-print-page]")];
  for (const [i, p] of pages.entries()) {
    const url = await toJpeg(p, { quality: 0.95, backgroundColor: "#ffffff", pixelRatio: 2 });
    const a = document.createElement("a");
    a.href = url;
    a.download = pages.length > 1 ? fileName(`${i + 1}.jpg`) : fileName("jpg");
    a.click();
  }
};

const PrintTable = ({ list, cols, innerRef }) => {
  const groupes = decouper(cols, (A4_W - 48) / PX_PAR_CAR);
  return (
    <div ref={innerRef} className="pointer-events-none fixed -left-[9999px] top-0">
      {groupes.map((g, i) => {
        const total = g.reduce((n, [, , w]) => n + w, 0);
        return (
        <div key={i} data-print-page className="overflow-hidden bg-white p-6 text-black" style={{ width: A4_W, minHeight: A4_H, fontFamily: "Arial, sans-serif" }}>
          <div className="mb-3 flex items-center gap-3">
            <img src={LOGO} alt="Logo USC Jemappes" className="h-14 w-auto" />
            <div>
              <h2 className="mb-0.5 text-lg font-bold">{TITRE}{groupes.length > 1 ? ` (${i + 1}/${groupes.length})` : ""}</h2>
              <p className="text-[10px] font-semibold text-red-700">{CONFIDENTIEL}</p>
            </div>
          </div>
          <table className="w-full table-fixed border-collapse text-[10px] leading-tight">
            <colgroup>{g.map(([l, , w]) => <col key={l} style={{ width: `${(w / total) * 100}%` }} />)}</colgroup>
            <thead><tr>{g.map(([l]) => <th key={l} className="border border-gray-400 bg-[#0A1628] px-1 py-1 text-left text-[#D4AF37]">{l}</th>)}</tr></thead>
            <tbody>{rowsFor(list, g).map((r, k) => <tr key={k} style={list[k]?.staff ? { background: "#FDF0C4", fontWeight: 700 } : undefined}>{r.map((c, m) => <td key={m} className="break-words border border-gray-300 px-1 py-0.5 align-top">{c}</td>)}</tr>)}</tbody>
          </table>
        </div>
        );
      })}
    </div>
  );
};

const Dash = () => <span className="text-dim">—</span>;

export const maskTel = (t) => {
  const d = (t ?? "").replace(/\D/g, "");
  return d.length < 5 ? "••••" : `${d.slice(0, 2)}•• •• •• ${d.slice(-2)}`;
};
export const maskMail = (m) => {
  const [u, dom] = (m ?? "").split("@");
  return dom ? `${u.charAt(0)}•••@${dom}` : "•••";
};

const RespCell = ({ lien, nom, tel, email, adresse }) => {
  const [show, setShow] = useState(false);
  if (!lien && !nom && !tel && !email && !adresse) return <Dash />;
  return (
    <div className="space-y-0.5">
      <p className="text-chalk">{lien && <span className="mr-1 rounded-full border border-lime/60 px-1.5 py-px font-mono text-[11px] uppercase text-lime">{lien}</span>}{nom}</p>
      {adresse && <p className="break-words text-xs text-dim">{adresse}</p>}
      {(tel || email) && (
        <div className="flex items-start gap-2">
          <div data-testid="contact-values" className="min-w-0">
            {tel && (show ? <a href={`tel:${tel}`} className="block text-chalk hover:text-lime">{tel}</a> : <span className="block font-mono text-chalk">{maskTel(tel)}</span>)}
            {email && (show ? <a href={`mailto:${email}`} className="block break-all text-dim hover:text-lime">{email}</a> : <span className="block break-all text-dim">{maskMail(email)}</span>)}
          </div>
          <button type="button" data-testid="contact-reveal-button" onClick={() => setShow((s) => !s)} aria-label={show ? "Masquer les coordonnées" : "Afficher les coordonnées"} title={show ? "Masquer" : "Afficher"} className="mt-0.5 shrink-0 text-dim transition-colors hover:text-lime">
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      )}
    </div>
  );
};

const MedicalCell = ({ j }) => (!j.medecin_nom && !j.medecin_tel && !j.medical && santeLignes(j).length === 0 ? <Dash /> : <>
  {(j.medecin_nom || j.medecin_tel) && <p className="text-xs"><span className="text-dim">Dr </span>{j.medecin_nom.replace(/^dr\.?\s+/i, "")} {j.medecin_tel && <a href={`tel:${j.medecin_tel}`} className="block text-chalk hover:text-lime">{j.medecin_tel}</a>}</p>}
  {j.medecin_adresse && <p className="break-words text-xs text-dim">{j.medecin_adresse}</p>}
  {santeLignes(j).map(([l, t]) => <p key={l} className={`mt-1 break-words text-xs ${t === "Non" ? "text-dim" : "rounded-lg border border-signal/50 bg-signal/10 px-2 py-1"}`}><span className="font-semibold">{l} :</span> {t}</p>)}
  {j.medical && <p className="mt-1 whitespace-pre-wrap break-words rounded-lg border border-signal/50 bg-signal/10 px-2 py-1 text-xs">{j.medical}</p>}
</>);
// Si le contact d'urgence n'est pas un des responsables (« Autre »), on affiche son nom et son numéro.
const UrgenceCell = ({ j }) => {
  const u = urgenceOf(j);
  if (!u) return <Dash />;
  const badge = <span className="rounded-full border border-signal/60 bg-signal/10 px-2 py-0.5 font-mono text-[11px] uppercase text-signal">{u.lien}</span>;
  if (j.urgence_choix !== "autre") return badge;
  return (
    <div data-testid={`urgence-autre-${j.id}`} className="space-y-1">
      {badge}
      <RespCell nom={u.nom} tel={u.tel} />
      {u.tel2 && <RespCell tel={u.tel2} />}
    </div>
  );
};

const COLS = {
  adresse: { label: "Adresse", w: 180, cls: "break-words text-chalk", player: (j) => adresseComplete(j.adresse, j.cp_ville) || <Dash /> },
  resp1: { label: "Responsable 1", w: 210, player: (j) => <RespCell lien={j.resp1_lien} nom={j.responsable} tel={j.telephone} email={j.email} adresse={j.resp1_adresse} />, staff: (s) => <RespCell tel={s.telephone} email={s.email} /> },
  resp2: { label: "Responsable 2", w: 210, player: (j) => <RespCell lien={j.resp2_lien} nom={j.resp2_nom} tel={j.resp2_telephone} email={j.resp2_email} adresse={j.resp2_adresse} />, staff: () => <Dash /> },
  urgence: { label: "Urgence", w: 170, player: (j) => <UrgenceCell j={j} /> },
  medical: { label: "Médical", w: 230, cls: "text-chalk", player: (j) => <MedicalCell j={j} /> },
  taille: { label: "Taille maillot", w: 120, cls: "text-chalk", player: (j) => tailleTxt(j) || <Dash /> },
  remarques: { label: "Remarques", w: 230, cls: "whitespace-pre-wrap break-words text-chalk", player: (j) => j.remarques || <Dash /> },
  fiche: { label: "Fiche reçue", w: 110, cls: "font-mono text-xs text-chalk", player: (j) => frDate(j.fiche_date) || <Dash /> },
};
const TABS = [
  ["tout", "Tout", Object.keys(COLS)],
  ["famille", "Famille", ["resp1", "resp2"]],
  ["coordonnees", "Coordonnées", ["adresse", "urgence"]],
  ["sante", "Santé", ["medical"]],
  ["divers", "Divers", ["taille", "remarques", "fiche"]],
];
const NAME_W = 150, ACT_W = 64;

// Petit badge à côté du nom tant que la fiche de renseignements n'a pas été reçue.
export const SansFiche = ({ p, testid }) => (p.fiche_date ? null : (
  <span data-testid={testid} title="Fiche de renseignements pas encore remplie" className="ml-1 inline-flex items-center gap-0.5 whitespace-nowrap rounded-full border border-signal/70 bg-signal/15 px-1.5 py-px align-middle font-mono text-[10px] font-semibold uppercase text-signal">
    <FileWarning className="h-3 w-3" />Sans fiche
  </span>
));

const PlayerRow = ({ j, cols, onOpen, onEdit, onDelete }) => (
  <tr data-testid="player-row" className="border-t border-line align-top text-sm">
    <td className="sticky left-0 z-10 bg-pitch px-2 py-2 font-semibold text-chalk shadow-[4px_0_6px_-4px_rgba(0,0,0,0.6)]">
      <button type="button" data-testid={`player-open-${j.id}`} onClick={() => onOpen(j)} title="Voir la fiche" className="text-left underline decoration-line decoration-dotted underline-offset-4 transition-colors hover:text-lime hover:decoration-lime">{j.nom.toUpperCase()}</button>
      <CatBadge j={j} testid={`player-categorie-${j.id}`} />
      <SansFiche p={j} testid={`player-sans-fiche-${j.id}`} />
      <span className="block font-normal">{j.prenom}</span>
      {j.date_naissance && <span data-testid={`player-naissance-${j.id}`} className="mt-0.5 block font-mono text-[11px] font-normal text-dim">{frDate(j.date_naissance)}{age(j.date_naissance) !== null ? ` · ${age(j.date_naissance)} ans` : ""}</span>}
    </td>
    {cols.map((k) => <td key={k} data-testid={`player-${k}-${j.id}`} className={`px-2 py-2 ${COLS[k].cls ?? ""}`}>{COLS[k].player(j)}</td>)}
    <td className="hidden whitespace-nowrap px-2 py-2 text-right md:table-cell">
      <button data-testid={`player-edit-${j.id}`} onClick={() => onEdit(j)} aria-label="Modifier" className="p-1 text-dim hover:text-lime"><Pencil className="h-4 w-4" /></button>
      <button data-testid={`player-delete-${j.id}`} onClick={() => onDelete(j)} aria-label="Supprimer" className="p-1 text-dim hover:text-signal"><Trash2 className="h-4 w-4" /></button>
    </td>
  </tr>
);

const StaffRow = ({ s, cols, onOpen, onEdit, onDelete }) => (
  <tr data-testid="staff-effectif-row" className="border-t border-lime/30 bg-lime/10 align-top text-sm">
    <td className="sticky left-0 z-10 border-l-4 border-lime bg-[#1d2a2c] px-2 py-2 font-semibold text-chalk shadow-[4px_0_6px_-4px_rgba(0,0,0,0.6)]">
      <button type="button" data-testid={`staff-open-${s.id}`} onClick={() => onOpen(s)} title="Voir la fiche" className="text-left underline decoration-line decoration-dotted underline-offset-4 transition-colors hover:text-lime hover:decoration-lime">{s.nom.toUpperCase()}</button>
      <SansFiche p={s} testid={`staff-sans-fiche-${s.id}`} />
      <span className="mt-0.5 flex items-center gap-1 font-mono text-[11px] uppercase text-lime"><ShieldCheck className="h-3.5 w-3.5" />{s.fonction || "Encadrement"}</span>
      {s.date_naissance && <span className="mt-0.5 block font-mono text-[11px] font-normal text-dim">{frDate(s.date_naissance)}{age(s.date_naissance) !== null ? ` · ${age(s.date_naissance)} ans` : ""}</span>}
    </td>
    {cols.map((k, i) => {
      if (k === "resp2" && cols[i - 1] === "resp1") return null;
      if (k === "resp1") {
        const span = cols[i + 1] === "resp2" ? 2 : 1;
        return (
          <td key={k} colSpan={span} data-testid={`staff-coordonnees-${s.id}`} className="px-2 py-2">
            <span className="mb-0.5 block font-mono text-[11px] uppercase text-lime">Coordonnées perso</span>
            <RespCell tel={s.telephone} email={s.email} />
          </td>
        );
      }
      return <td key={k} data-testid={`staff-${k}-${s.id}`} className={`px-2 py-2 ${COLS[k].cls ?? ""}`}>{(COLS[k].staff ?? COLS[k].player)(s)}</td>;
    })}
    <td className="hidden whitespace-nowrap px-2 py-2 text-right md:table-cell">
      <button data-testid={`staff-effectif-edit-${s.id}`} onClick={() => onEdit(s)} aria-label="Modifier" className="p-1 text-dim hover:text-lime"><Pencil className="h-4 w-4" /></button>
      <button data-testid={`staff-effectif-delete-${s.id}`} onClick={() => onDelete(s)} aria-label="Supprimer" className="p-1 text-dim hover:text-signal"><Trash2 className="h-4 w-4" /></button>
    </td>
  </tr>
);

export const PlayersDirectory = ({ code, convocationId }) => {
  const [list, setList] = useState([]);
  const [staff, setStaff] = useState([]);
  const [staffForm, setStaffForm] = useState(null);
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState("tout");
  const [sheet, setSheet] = useState(null);
  const [adding, setAdding] = useState(false);
  const [exportSel, setExportSel] = useState(loadExportCols);
  const printRef = useRef(null);
  const cols = TABS.find(([k]) => k === tab)[2];
  const exportCols = EXPORT_COLS.filter(([l]) => exportSel.includes(l));
  const changeExportSel = (sel, filtre = false) => { setExportSel(sel); setSansFiche(filtre); localStorage.setItem(COLS_KEY, JSON.stringify(sel)); };
  const [sansFiche, setSansFiche] = useState(false);
  const params = convocationId ? { convocation_id: convocationId } : {};
  const load = useCallback(() => {
    api.get("/joueurs", { ...coachHeaders(code), params: convocationId ? { convocation_id: convocationId } : {} }).then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [code, convocationId]);
  useEffect(() => { load(); }, [load]);
  const loadStaff = useCallback(() => {
    api.get("/encadrement", coachHeaders(code)).then((r) => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [code]);
  useEffect(() => { loadStaff(); }, [loadStaff]);
  const exportList = sansFiche ? list.filter((j) => !j.fiche_date) : [...staff.map(staffAsRow), ...list];

  const submitStaff = async (s) => {
    if (!s.nom.trim()) { toast.error("Nom obligatoire"); return false; }
    try {
      if (s.id) await api.put(`/encadrement/${s.id}`, s, coachHeaders(code));
      else await api.post("/encadrement", s, coachHeaders(code));
      toast.success("Encadrement enregistré");
      loadStaff();
      return true;
    } catch (err) { toast.error(errMsg(err)); return false; }
  };
  const removeStaff = async (s) => {
    if (!window.confirm(`Retirer ${s.nom} de l'encadrement ?`)) return;
    await api.delete(`/encadrement/${s.id}`, coachHeaders(code));
    loadStaff();
  };

  const submit = async (f) => {
    if (!f.nom.trim() || !f.prenom.trim()) { toast.error("Nom et prénom obligatoires"); return false; }
    try {
      if (f.id) await api.put(`/joueurs/${f.id}`, f, coachHeaders(code));
      else await api.post("/joueurs", f, { ...coachHeaders(code), params });
      toast.success(f.id ? "Joueur modifié" : "Joueur ajouté");
      load();
      return true;
    } catch (err) { toast.error(errMsg(err)); return false; }
  };
  const invite = async (nouveau) => {
    const existant = list.find((x) => joueurKey(x.nom, x.prenom) === joueurKey(nouveau.nom, nouveau.prenom));
    const j = existant ? { ...existant, telephone: nouveau.telephone || existant.telephone } : { ...nouveau, fiche_token: crypto.randomUUID().replace(/-/g, "") };
    envoyerFicheParent(j);
    if (existant) { toast.info(`${j.prenom} est déjà dans l'effectif – lien de sa fiche envoyé au parent`); setAdding(false); return; }
    try {
      await api.post("/joueurs", j, { ...coachHeaders(code), params });
      toast.success(`${j.prenom} ajouté(e) – fiche envoyée au parent`);
    } catch (err) { toast.error(errMsg(err)); return; }
    setAdding(false);
    load();
  };
  const remove = async (j) => {
    if (!window.confirm(`Supprimer ${j.prenom} ${j.nom} de l'effectif ?`)) return;
    await api.delete(`/joueurs/${j.id}`, coachHeaders(code));
    load();
  };
  const run = (fn) => async () => {
    if (exportList.length === 0) { toast.error(sansFiche ? "Tous les enfants ont envoyé leur fiche" : "L'effectif est vide"); return; }
    if (exportCols.length === 0) { toast.error("Choisis au moins une colonne à exporter"); return; }
    try { await fn(); } catch { toast.error("Export impossible"); }
  };
  const btn = "inline-flex items-center gap-1 rounded-full border border-lime px-3 py-2 text-sm font-semibold text-lime hover:bg-lime hover:text-ink";
  const th = "px-2 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-dim";

  return (
    <div data-testid="players-directory" className="rounded-2xl border border-line bg-pitch p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-2xl uppercase text-chalk">Effectif ({list.length})</h2>
        <div className="flex flex-wrap gap-2">
          <button data-testid="player-add-button" onClick={() => setAdding(true)} className="hidden items-center gap-1 rounded-full bg-lime px-4 py-2 text-sm font-semibold text-ink md:inline-flex"><Plus className="h-4 w-4" />Ajouter</button>
          <ExportColumnsPicker labels={EXPORT_LABELS} presets={PRESETS} selected={exportSel} sansFiche={sansFiche} onChange={changeExportSel} className={btn} />
          <button data-testid="export-pdf-button" onClick={run(() => exportTableauA4(exportList, exportCols, rowsFor, fileName("pdf")))} className={btn}><FileText className="h-4 w-4" />PDF</button>
          <button data-testid="export-fiches-button" title="Une page A4 par enfant" onClick={run(() => exportFichesA4(exportList.filter((j) => !j.staff), exportCols, SECTIONS, "fiches-enfants-usc-jemappes-u12c.pdf"))} className={btn}><FileText className="h-4 w-4" />Fiches A4</button>
          <button data-testid="export-excel-button" onClick={run(() => exportExcel(exportList, exportCols))} className={btn}><FileSpreadsheet className="h-4 w-4" />Excel</button>
          <button data-testid="export-jpeg-button" onClick={run(() => exportJpeg(printRef.current))} className={btn}><ImageIcon className="h-4 w-4" />JPEG</button>
        </div>
      </div>
      <p data-testid="effectif-mobile-note" className="mt-3 flex items-start gap-2 rounded-xl border border-line bg-ink/60 px-3 py-2 text-sm text-chalk md:hidden"><Monitor className="mt-0.5 h-4 w-4 shrink-0 text-lime" />Sur téléphone, l'effectif est en consultation : touchez un nom pour voir la fiche et appeler. Ajout, modification et suppression se font sur ordinateur.</p>
      <div role="tablist" data-testid="effectif-tabs" className="mt-4 flex gap-2 overflow-x-auto pb-1">
        {TABS.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} data-testid={`effectif-tab-${k}`} onClick={() => setTab(k)}
            className={`shrink-0 rounded-full border px-3 py-1.5 font-mono text-xs uppercase tracking-[0.12em] transition-colors ${tab === k ? "border-lime bg-lime text-ink" : "border-line text-chalk hover:border-lime hover:text-lime"}`}>{label}</button>
        ))}
      </div>
      <div className="mt-2 overflow-x-auto">
        <table data-testid="effectif-table" className="w-full table-fixed text-left" style={{ minWidth: NAME_W + ACT_W + cols.reduce((n, k) => n + COLS[k].w, 0) }}>
          <colgroup><col style={{ width: NAME_W }} />{cols.map((k) => <col key={k} style={{ width: COLS[k].w }} />)}<col className="hidden md:table-column" style={{ width: ACT_W }} /></colgroup>
          <thead><tr><th className={`${th} sticky left-0 z-10 bg-pitch`}>Joueur</th>{cols.map((k) => <th key={k} data-testid={`effectif-col-${k}`} className={th}>{COLS[k].label}</th>)}<th className="hidden md:table-cell" /></tr></thead>
          <tbody>
            {staff.map((s) => <StaffRow key={s.id} s={s} cols={cols} onOpen={(x) => setSheet(staffAsRow(x))} onEdit={setStaffForm} onDelete={removeStaff} />)}
            {list.length === 0 && <tr><td colSpan={cols.length + 2} data-testid="players-empty" className="px-2 py-4 text-sm text-dim">Aucun joueur. Clique sur « Ajouter ».</td></tr>}
            {list.map((j) => <PlayerRow key={j.id} j={j} cols={cols} onOpen={setSheet} onEdit={setForm} onDelete={remove} />)}
          </tbody>
        </table>
      </div>
      {adding && <AddPersonDialog onClose={() => setAdding(false)} onStaff={() => { setAdding(false); setStaffForm({}); }} onManual={() => { setAdding(false); setForm({}); }} onInvite={invite} />}
      {form && <PlayerFormDialog open onOpenChange={(o) => { if (!o) setForm(null); }} initial={form} onSubmit={submit} />}
      {sheet && <PlayerSheet j={sheet} urgence={urgenceOf(sheet)} age={age} onClose={() => setSheet(null)}
        onExport={() => (sheet.staff ? exportFichesA4([sheet], STAFF_COLS, STAFF_SECTIONS, `fiche-${slugNom(sheet)}.pdf`) : exportFichesA4([sheet], exportCols.length ? exportCols : EXPORT_COLS, SECTIONS, `fiche-${slugNom(sheet)}.pdf`)).catch(() => toast.error("Export impossible"))}
        onSendFiche={() => (sheet.staff ? envoyerFicheEncadrant(sheet) : envoyerFicheParent(sheet))}
        onEdit={(j) => { setSheet(null); if (j.staff) setStaffForm(staff.find((s) => s.id === j.id) ?? null); else setForm(j); }} />}
      {staffForm && <EncadrantDialog initial={staffForm} onClose={() => setStaffForm(null)} onSubmit={submitStaff} />}
      <PrintTable list={exportList} cols={exportCols} innerRef={printRef} />
    </div>
  );
};
